import React, { useState } from "react";
import { FiHeart, FiShoppingCart, FiEye, FiTrash2 } from "react-icons/fi";
import { FaHeart } from "react-icons/fa";
import { toast } from "react-hot-toast";
import { Link } from "react-router-dom";
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from "../../services/apiClient";
import { IMAGE_PATH } from "../../utils/constants";

const HomeProduct = () => {
  const [hoveredProduct, setHoveredProduct] = useState(null);
  const [imageHovered, setImageHovered] = useState(null);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [cartItems, setCartItems] = useState([]);
  const queryClient = useQueryClient();

  // React Query for fetching products
  const { data: products = [], isLoading, error } = useQuery({
    queryKey: ['home-products'],
    queryFn: async () => {
      const response = await apiClient.get('/products-list', {
        params: {
          per_page: '8',
          sort_by: 'created_at',
          sort_order: 'desc',
        }
      });
      return response.data.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh for 5 mins
    cacheTime: 10 * 60 * 1000, // 10 minutes - cache persists for 10 mins
  });

  // Show error toast if fetch fails
  React.useEffect(() => {
    if (error) {
      console.error("Error fetching products:", error);
      toast.error("Failed to load products");
    }
  }, [error]);

  const toggleWishlist = (productId) => {
    if (wishlistItems.includes(productId)) {
      setWishlistItems(wishlistItems.filter(id => id !== productId));
      toast.success("Removed from wishlist");
    } else {
      setWishlistItems([...wishlistItems, productId]);
      toast.success("Added to wishlist");
    }
  };

  const toggleCart = (productId) => {
    if (cartItems.includes(productId)) {
      setCartItems(cartItems.filter(id => id !== productId));
      toast.success("Removed from cart");
    } else {
      setCartItems([...cartItems, productId]);
      toast.success("Added to cart");
    }
  };

  const ProductSkeleton = () => (
    <div className='ProductBox'>
      <div className="productLink">
        <div className='ProductImage'>
          <Skeleton height="100%" />
        </div>
        <div className="ProductText">
          <h3><Skeleton width={120} /></h3>
          <div className="priceContainer">
            <p className="originalPrice"><Skeleton width={60} /></p>
            <span className="offerPrice"><Skeleton width={50} /></span>
          </div>
        </div>
      </div>
    </div>
  );

  // Function to get the first two images for a product
  const getProductImages = (product) => {
    if (product.is_combo) {
      return {
        image1: product.image_path,
        image2: product.products[0]?.media[0]?.file_path,
      };
    } else {
      if (!product?.media || product?.media.length === 0) {
        return { image1: '', image2: '' };
      }
      return {
        image1: product?.media[0].file_path,
        image2: getSecondProductImage(product)
      };
    }
  };

  const getSecondProductImage = (product) => {
    for (let x = 1 ; x <= product.media.length ; x++) {
      
      const imageTypes = ['image/jpeg','image/png','image/webp'];
      if (imageTypes.includes(product.media[x]?.file_type)) {
        return `${product.media[x].file_path}`;
      }
    }
  };

  function getPrice(product) {
    if (product.is_combo) {
      return {
        price: product.products.reduce((total, p) => total + parseFloat(p.price), 0),
        salePrice: product.combo_price,
      }
    } else {
      return {
        price: product.price,
        salePrice: product.sale_price,
      }
    }
  }

  return (
    <div id="HomeProduct">
      <div className="container">
        <div className="homeProductMain">
          <div className="homeProductTitle">
            <h2 className="homeProductTitleText">New Arrivals</h2>
            <p className="homeProductDesc">
              Discover our thoughtfully curated wellness essentials designed to support balance, relaxation, 
              and everyday well-being. Nurture your mind, body, and spirit—naturally.
            </p>

          </div>
          <div className="homProductCardList">
            {isLoading ? (
              Array(8).fill(0).map((_, index) => (
                <ProductSkeleton key={`skeleton-${index}`} />
              ))
            ) : (
              products.map((product) => {
                const { image1, image2 } = getProductImages(product);
                const { price, salePrice } = getPrice(product);
                return (
                  <div
                    className='ProductBox'
                    key={product.id}
                    onMouseEnter={() => setHoveredProduct(product.id)}
                    onMouseLeave={() => {
                      setHoveredProduct(null);
                      setImageHovered(null);
                    }}
                  >

                    <Link to={product.is_combo ? `/combo/${product.id}` : `/products/${product.id}`} className="productLink">
                      <div
                        className='ProductImage'
                        onMouseEnter={() => setImageHovered(product.id)}
                        onMouseLeave={() => {
                          if (hoveredProduct !== product.id) {
                            setImageHovered(null);
                          }
                        }}
                      >
                        {image1 && (
                          <img
                            src={IMAGE_PATH + image1}
                            alt={product.is_combo ? product.title : product.name}
                            className={imageHovered === product.id ? 'fade-out' : 'fade-in'}
                            loading="lazy"
                          />
                        )}
                        {image2 && (
                          <img
                            src={IMAGE_PATH + image2}
                            alt={product.name}
                            className={imageHovered === product.id ? 'fade-in' : 'fade-out'}
                            loading="lazy"
                          />
                        )}
                      </div>

                      <div className="ProductText">
                        <h3>{product.is_combo ? product.title : product.name}</h3>
                        <div className="priceContainer">
                          {price && price !== salePrice ? (
                            <>
                              <p className="originalPrice">₹{price}</p>
                              <span className="offerPrice">₹{salePrice}</span>
                            </>
                          ) : (
                            <span className="offerPrice">₹{salePrice}</span>
                          )}
                        </div>
                      </div>
                    </Link>

                    <div
                      className={`productIcons ${hoveredProduct === product.id ? 'visible' : ''}`}
                      onMouseEnter={(e) => {
                        e.stopPropagation();
                        setHoveredProduct(product.id);
                      }}
                    >
                      <div
                        className="iconBox"
                        data-tooltip={wishlistItems.includes(product.id) ? "Remove from wishlist" : "Add to wishlist"}
                        onClick={(e) => {
                          e.preventDefault();
                          toggleWishlist(product.id);
                        }}
                      >
                        {wishlistItems.includes(product.id) ? (
                          <FaHeart className="icon" style={{ color: 'red' }} />
                        ) : (
                          <FiHeart className="icon" />
                        )}
                      </div>

                      <div
                        className="iconBox"
                        data-tooltip={cartItems.includes(product.id) ? "Remove from cart" : "Add to cart"}
                        onClick={(e) => {
                          e.preventDefault();
                          toggleCart(product.id);
                        }}
                      >
                        {cartItems.includes(product.id) ? (
                          <FiTrash2 className="icon" />
                        ) : (
                          <FiShoppingCart className="icon" />
                        )}
                      </div>

                      <div className="iconBox" data-tooltip="Quick View">
                        <FiEye className="icon" />
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeProduct;